import { Link } from 'react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { DealList, DealSummary, LeadList, LeadStatus, LeadSummary, PipelineStage } from '@agency/contracts';
import { Button, Skeleton } from '@agency/ui';
import { ApiClientError, get } from '../../lib/api.ts';
import { relative, RESTRICTED } from '../../lib/format.ts';
import { QueryError } from '../../lib/query-state.tsx';

const BEFORE: { status: LeadStatus; label: string; empty: string }[] = [
  { status: 'NEW', label: 'New', empty: 'No new inquiries.' },
  { status: 'QUALIFIED', label: 'Qualified', empty: 'No qualified leads.' },
];
const AFTER: { status: LeadStatus; label: string; empty: string }[] = [
  { status: 'WON', label: 'Won', empty: 'No won deals yet.' },
  { status: 'LOST', label: 'Lost', empty: 'No lost leads.' },
];

function LeadCard({ lead }: { lead: LeadSummary }) {
  const title = lead.piiRedacted ? RESTRICTED : lead.name ?? lead.company ?? 'Unnamed lead';
  const detail = new URLSearchParams({ lead: lead.id });
  return (
    <li>
      <Link className="pipeline-card" to={`/leads?${detail}`}>
        <strong>{title}</strong>
        <span className="pipeline-card__sub">{lead.piiRedacted ? 'Contact details hidden for your role' : lead.company ?? lead.email ?? 'No company recorded'}</span>
        <span className="pipeline-card__meta">{lead.landingName} · v{lead.landingVersionNumber}</span>
        <span className="pipeline-card__foot">
          <span>{lead.assigneeName ?? 'Unassigned'}</span>
          <span title={lead.createdAt}>{relative(lead.createdAt)}</span>
        </span>
        {lead.deal ? <span className="pipeline-card__amount">{lead.deal.amount} {lead.deal.currency}</span> : null}
      </Link>
    </li>
  );
}

function LeadColumn({ status, label, empty }: { status: LeadStatus; label: string; empty: string }) {
  const query = useInfiniteQuery<LeadList, ApiClientError>({
    queryKey: ['pipeline', 'lead', status],
    initialPageParam: '',
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ status, limit: '25' });
      if (pageParam) params.set('cursor', String(pageParam));
      return get<LeadList>(`/leads?${params}`);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];
  const count = query.data?.pages[0]?.counts[status];

  return (
    <section className="pipeline-column" aria-labelledby={`pipeline-${status}`}>
      <header className="pipeline-column__head">
        <h2 id={`pipeline-${status}`}>{label}</h2>
        <span aria-label={count === undefined ? 'Count unavailable' : `${count} leads`}>{count ?? '–'}</span>
      </header>
      {query.isLoading ? (
        <div className="pipeline-column__loading" role="status"><span className="ag-visually-hidden">Loading {label.toLowerCase()} leads…</span><Skeleton height={110} /><Skeleton height={110} /></div>
      ) : query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} />
      ) : items.length ? (
        <>
          <ul className="pipeline-column__cards">{items.map((lead) => <LeadCard key={lead.id} lead={lead} />)}</ul>
          {query.hasNextPage ? <div className="pipeline-column__more"><Button onClick={() => void query.fetchNextPage()} loading={query.isFetchingNextPage}>Load more</Button></div> : null}
        </>
      ) : <p className="pipeline-column__empty">{empty}</p>}
    </section>
  );
}

function DealCard({ deal }: { deal: DealSummary }) {
  return (
    <li><Link className="pipeline-card" to={`/deals?${new URLSearchParams({ deal: deal.id })}`}>
      <strong>{deal.title}</strong>
      <span className="pipeline-card__sub">{deal.piiRedacted ? 'Contact details hidden for your role' : deal.company ?? deal.contactName ?? 'No company recorded'}</span>
      <span className="pipeline-card__meta">{deal.landingName} · v{deal.landingVersionNumber}</span>
      <span className="pipeline-card__foot"><span>{deal.ownerName ?? 'Unassigned'}</span><span title={deal.proposedAt}>{relative(deal.proposedAt)}</span></span>
      <span className="pipeline-card__amount">{deal.amount} {deal.currency}</span>
      {deal.expectedCloseDate ? <span className="pipeline-card__meta">Expected close {deal.expectedCloseDate}</span> : null}
    </Link></li>
  );
}

function DealColumn({ stage }: { stage: PipelineStage }) {
  const query = useInfiniteQuery<DealList, ApiClientError>({
    queryKey: ['pipeline', 'deal', stage.id],
    initialPageParam: '',
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ status: 'PROPOSAL', stageId: stage.id, limit: '25' });
      if (pageParam) params.set('cursor', String(pageParam));
      return get<DealList>(`/deals?${params}`);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];
  const count = query.data?.pages[0]?.filteredCount;
  return (
    <section className="pipeline-column" aria-labelledby={`pipeline-stage-${stage.id}`}>
      <header className="pipeline-column__head"><h2 id={`pipeline-stage-${stage.id}`}>{stage.name}</h2><span aria-label={count === undefined ? 'Count unavailable' : `${count} deals`}>{count ?? '–'}</span></header>
      {query.isLoading ? <div className="pipeline-column__loading" role="status"><span className="ag-visually-hidden">Loading {stage.name} deals…</span><Skeleton height={110} /><Skeleton height={110} /></div> : query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : items.length ? <>
        <ul className="pipeline-column__cards">{items.map((deal) => <DealCard key={deal.id} deal={deal} />)}</ul>
        {query.hasNextPage ? <div className="pipeline-column__more"><Button onClick={() => void query.fetchNextPage()} loading={query.isFetchingNextPage}>Load more</Button></div> : null}
      </> : <p className="pipeline-column__empty">No deals in this stage.</p>}
    </section>
  );
}

export function PipelinePage() {
  const stages = useQuery<PipelineStage[], ApiClientError>({ queryKey: ['pipeline-stages'], queryFn: () => get('/pipeline/stages') });
  return (
    <>
      <div className="pagehead"><div><h1>Pipeline</h1><p>Follow inquiries into configurable deal stages and final outcomes. Select a card to work its record.</p></div></div>
      {stages.isLoading ? <div role="status"><span className="ag-visually-hidden">Loading pipeline stages…</span><Skeleton height={320} /></div> : stages.isError ? <QueryError error={stages.error} onRetry={() => void stages.refetch()} /> : <div className="pipeline-board" aria-label="Sales pipeline">
        {BEFORE.map((stage) => <LeadColumn key={stage.status} {...stage} />)}
        {stages.data?.filter((stage) => stage.active).map((stage) => <DealColumn key={stage.id} stage={stage} />)}
        {AFTER.map((stage) => <LeadColumn key={stage.status} {...stage} />)}
      </div>}
      <p className="pipeline-note">Spam is kept in the Leads queue. Counts reflect only records visible to your role. Amounts retain their original currencies.</p>
    </>
  );
}
